import {
  Button,
  TextField,
  Grid,
  Typography,
  InputAdornment,
} from "@mui/material";
import useFormStyles from "../../styles/useFormStyles";
import { useState, ChangeEvent, useEffect } from "react";
import useButtonStyles from "../../styles/useButtonStyles";
import { useTheme } from "@mui/material/styles";
import { useRef } from "react";

export interface UploadImagePropTypes {
  setState: Function;
  stateKey: string;
  error?: boolean;
  disableSubmit?: Function;
  label?: string;
  clear?: boolean;
  value?: string;
  disable?: boolean;
  required?: boolean;
}

const UploadImage = (props: UploadImagePropTypes) => {
  const {
    setState,
    stateKey,
    error,
    disableSubmit,
    label,
    clear,
    value,
    disable,
    required,
  } = props;
  const theme = useTheme();
  const [errorState, setErrorState] = useState<boolean>(error ? error : false);
  const [image, setImage] = useState(value);
  const [message, setMessage] = useState("");
  const formStyles = useFormStyles();
  const buttonStyles = useButtonStyles();
  const [selectedFile, setSelectedFile] = useState<File>();
  const [base64String, setBase64String] = useState<string>("");
  const imageInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event?.target?.files?.[0];
    if (file) {
      if (file.size && file.size <= 3 * 1024 * 1024) {
        // Image size is less than or equal to 3MB
        setImage("");
        if (file.type?.includes("image")) {
          if (disableSubmit) disableSubmit(false);
          setErrorState(false);
        } else {
          setMessage("(Please upload a valid image)");
          if (disableSubmit) disableSubmit(true);
          setErrorState(true);
        }
      } else {
        setMessage("(Image size exceeds 3mb)");
        // Image size exceeds 3MB
        if (disableSubmit) disableSubmit(true);
        setErrorState(true);
      }
    }

    setSelectedFile(file);
    const base64: any = await toBase64(file);
    setBase64String(base64);
  };

  useEffect(() => {
    setErrorState(error ? error : false);
    if (clear) {
      if (imageInputRef.current) {
        setSelectedFile(undefined);
        imageInputRef.current.value = "";
      }
      setBase64String("");
    }
  }, [error, clear]);

  const toBase64 = (file: any) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      if (file) {
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
      }
    });

  useEffect(() => {
    setState((prev: any) => {
      return { ...prev, [stateKey]: base64String };
    });
  }, [base64String]);

  return (
    <Grid
      display={"flex"}
      flexDirection={"column"}
      justifyContent={"space-between"}
    >
      <Grid gap={1} display={"flex"}>
        <TextField
          label={label}
          error={error}
          required={required}
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                {selectedFile && (
                  <img
                    src={base64String}
                    alt="Uploaded File"
                    style={{
                      width: "60px",
                      height: "25px",
                      borderRadius: "4px",
                    }}
                  />
                )}
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  sx={{ ...buttonStyles.textSecondary }}
                  component="label"
                  disabled={disable}
                >
                  Upload Image
                  <input
                    ref={imageInputRef}
                    onChange={handleFileSelect}
                    type="file"
                    hidden
                  />
                </Button>
              </InputAdornment>
            ),
            sx: {
              ...formStyles.formLabel,
              color: errorState
                ? theme?.color?.failure ?? "red"
                : theme?.color?.dividerMedium ?? "black",
              width: "100%", // Ensure the input takes up the entire width
            },
          }}
          value={selectedFile ? "" : image}
          size="small"
          disabled={disable}
          sx={{
            border: errorState ? "1px solid red" : "none",
            borderRadius: errorState ? "4px" : "0px",
          }}
        />
      </Grid>
      {/* <Dialog open={sizeWrong} onClose={() => setSizeWrong(false)}>
        <DialogTitle
          sx={{
            fontWeight: "600",
            padding: 4,
            pb: 2,
            color: theme.color?.error,
            textAlign: "center",
          }}
        >
          Error
        </DialogTitle>
        <DialogContent sx={{ px: 4 }}>
          <Typography>Image size more than 3Mb</Typography>
        </DialogContent>
      </Dialog> */}
      <Grid>
        <Typography
          sx={{
            p: 0.5,
            textTransform: "uppercase",
            fontFamily: theme.font?.fontFamily.primary,
            fontSize: theme.font?.fontSize.tableBody,
            color: errorState ? theme.color?.error : theme.color?.sideGrey,
          }}
        >
          {errorState ? message : "(maximum upload size of 3mb)"}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default UploadImage;
